import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Button, Card, Input } from '../design-system';
import { apiPost } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import analyticsService from '../../services/analyticsService';
import toast from 'react-hot-toast';

const AddPatientModal = ({ isOpen, onClose, onPatientAdded }) => {
  const { userInfo } = useAuth();
  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      name: '',
      email: '',
      notebookLink: '',
      context: '',
      hasConsent: false
    },
    shouldFocusError: false
  });

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  if (!isOpen) return null;

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data) => {
    console.log('📝 AddPatientModal: Form submitted', {
      email: data.email,
      name: data.name,
      hasConsent: data.hasConsent
    });
    
    try {
      console.log('📤 AddPatientModal: Sending API request to add-patient');
      const response = await apiPost('/.netlify/functions/add-patient', {
        email: data.email,
        name: data.name,
        notebookLink: data.notebookLink,
        context: data.context,
        hasConsent: data.hasConsent || false
      }, null, userInfo?.userId);
      
      console.log('✅ AddPatientModal: API response received', {
        status: response?.status,
        message: response?.message,
        patientId: response?.patient?.patientId,
        auth0AccountCreated: response?.auth0AccountCreated
      });
      
      // Track patient added event
      try {
        analyticsService.track('patient_added', {
          patientId: response?.patient?.patientId,
          hasNotebookLink: !!data.notebookLink,
          hasContext: !!data.context,
          hasConsent: data.hasConsent || false
        });
      } catch (analyticsError) {
        // Don't break patient addition if analytics fails
        console.error('Analytics tracking error:', analyticsError);
      }
      
      // Show success message from API (includes email notification info)
      toast.success(response?.message || 'Patient added successfully');
      reset();
      handleClose();
      if (onPatientAdded) {
        onPatientAdded();
      }
    } catch (error) {
      console.error('❌ AddPatientModal: Error adding patient', {
        errorMessage: error.message,
        errorStack: error.stack,
        errorDetails: error
      });
      
      if (error.message.includes('already exists')) {
        toast.error('A patient with this email already exists');
      } else if (error.message.includes('Network error')) {
        toast.error('Network error. Please check your connection and try again.');
      } else if (error.message.includes('Auth0 Management API configuration missing')) {
        toast.error('Server configuration error. Please contact support. (Auth0 not configured)');
        console.error('⚠️ Auth0 Management API environment variables are missing. Patient was created but Auth0 account was not.');
      } else {
        toast.error(error.message || 'Failed to add patient');
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4">
      <Card className="max-w-2xl w-full max-h-[95vh] md:max-h-[90vh] overflow-y-auto" variant="elevated">
        <div className="flex items-center justify-between p-4 md:p-6 border-b">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900">Add New Patient</h2>
          <Button
            onClick={handleClose}
            variant="ghost"
            size="small"
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <X className="h-5 w-5 md:h-6 md:w-6" />
          </Button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-4 md:p-6 space-y-4 md:space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              type="text"
              label="Name *"
              placeholder="Patient's name"
              error={!!errors.name}
              errorText={errors.name?.message}
              {...register('name', { 
                required: 'Name is required'
              })}
            />
            <Input
              type="email"
              label="Email *"
              placeholder="patient@example.com"
              error={!!errors.email}
              errorText={errors.email?.message}
              {...register('email', { 
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address'
                }
              })}
            />
          </div>

          <Input
            type="url"
            label="Notebook Link (Optional)"
            placeholder="https://notion.so/patient-notes or https://docs.google.com/..."
            helperText="Link to patient's notebook or notes (Notion, Google Docs, etc.)"
            {...register('notebookLink')}
          />

          <Input
            type="textarea"
            label="Context to Remember"
            placeholder="Add any important context about this patient that you'd like to remember..."
            helperText="This information will help you provide personalized care"
            rows={4}
            {...register('context')}
          />

          <div>
            <label className="flex items-start">
              <input
                type="checkbox"
                {...register('hasConsent', { required: 'Consent is required to store engagement data' })}
                className="h-4 w-4 border-gray-300 rounded focus:ring-2 mt-1"
                style={{
                  accentColor: 'var(--color-primary)'
                }}
              />
              <span className="ml-2 text-sm text-gray-700">
                I have obtained client consent to store engagement data. *
              </span>
            </label>
            {errors.hasConsent && (
              <div className="mt-1 text-sm text-red-600">
                {errors.hasConsent.message}
              </div>
            )}
          </div>

          <div className="flex space-x-3 pt-4">
            <Button
              type="submit"
              variant="primary"
              size="medium"
            >
              Add Patient
            </Button>
            <Button
              type="button"
              onClick={handleClose}
              variant="outline"
              size="medium"
            >
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default AddPatientModal;

